import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { Grid } from '@material-ui/core';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import TaskAttachmentCard from '../../tasks/cards/TaskAttachmentCard';
import { Task } from '../../tasks/Task.interface';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import TestStepCard from '../cards/TestStepCard';
import ValidationInfoCard from '../cards/ValidationInfoCard';
import ValidationUserNeedReferenceCard from '../cards/ValidationUserNeedReferenceCard';
import {
  useFindProduct,
  useFindProductValidations,
  useProductFilterQueryParams
} from '../Product.hooks';
import {
  BaseProductValidationCardProps,
  ProductEntity,
  ProductValidation
} from '../Product.interface';
import ProductDocumentCommonPage from './ProductDocumentCommonPage';

const ProductValidationDocumentPage: React.FC = () => {
  const { productId: paramProductId = '' } = useParams<{
    productId?: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [productValidations, setProductValidations] = useState<
    ProductValidation[]
  >([]);
  const {
    productValidationList,
    loadingProductValidationsError,
    findProductValidations
  } = useFindProductValidations(productId);
  const { product } = useFindProduct(productId, true);
  const { getParams } = useProductFilterQueryParams();

  const createCardProps = (
    productEntity: ProductEntity
  ): BaseProductValidationCardProps => {
    const validation: ProductValidation = productEntity as ProductValidation;
    return {
      productId,
      productValidation: validation,
      onUpdate: () => setDidUpdate(true),
      onError: (error: FetchError) => setError(error),
      onChange: (newProductValidation: Partial<ProductValidation>) => {
        const selectedIndex: number = productValidations.findIndex(
          (un: ProductValidation) => un.id === validation.id
        );
        if (selectedIndex > -1) {
          const validations: ProductValidation[] = [...productValidations];
          validations[selectedIndex] = {
            ...validation,
            ...newProductValidation
          };
          setProductValidations(validations);
        }
      }
    };
  };

  useEffect(() => {
    findProductValidations({ search, ...getParams() });
  }, [findProductValidations, search, getParams]);

  useEffect(() => {
    if (productValidationList) {
      setProductValidations(productValidationList.data);
    }
  }, [productValidationList]);

  useEffect(() => {
    setError(loadingProductValidationsError);
  }, [loadingProductValidationsError]);

  return (
    <>
      <ProductDocumentCommonPage
        product={product}
        productEntities={productValidations}
        entity={ProductBoardColumnEntity.Validation}
        renderInfoCard={(
          productEntity: ProductEntity,
          title: string,
          hidden?: boolean
        ) => {
          const props: BaseProductValidationCardProps = createCardProps(
            productEntity
          );
          return (
            <>
              <ValidationInfoCard
                {...props}
                title={title}
                hidden={hidden}
                enableTitleRedirect
              />
              <TestStepCard
                validationId={productEntity.id}
                hidden={hidden}
                onError={props.onError}
                onUpdate={props.onUpdate}
              />
            </>
          );
        }}
        renderGridCards={(productEntity: ProductEntity) => {
          const props: BaseProductValidationCardProps = createCardProps(
            productEntity
          );
          return (
            <>
              <Grid item xs={12}>
                <ValidationUserNeedReferenceCard {...props} />
              </Grid>
              {productEntity.task && (
                <Grid item xs={12}>
                  <TaskAttachmentCard
                    compact
                    task={productEntity.task}
                    onError={props.onError}
                    onChange={(newTask: Partial<Task>) => {
                      Object.assign(productEntity.task, newTask);
                      // update with new task
                      props.onChange?.({});
                      props.onUpdate?.();
                    }}
                  />
                </Grid>
              )}
            </>
          );
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product Validation details updated.
      </AppSnackbar>
    </>
  );
};

export default ProductValidationDocumentPage;
